import { sanitizeRedirectPath } from './templateSeo.js';

const STORAGE_KEY = 'laudando_onboarding_v1';
const SAFE_TEMPLATE_REF = /^[a-z0-9][a-z0-9_-]{0,79}$/i;
const SAFE_USER_KEY = /^[a-z0-9][a-z0-9_-]{0,127}$/i;
const VALID_STATUSES = new Set(['pending', 'dismissed', 'activated']);

const onboardingStorageKey = (userId) => {
  const safeUser = String(userId || '').trim();
  return SAFE_USER_KEY.test(safeUser) ? `${STORAGE_KEY}:${safeUser}` : `${STORAGE_KEY}:anonymous`;
};

export const sanitizeOnboardingTemplateRef = (value) => {
  const ref = String(value || '').trim();
  return SAFE_TEMPLATE_REF.test(ref) ? ref : null;
};

export const buildOnboardingPath = ({ templateRef = null } = {}) => {
  const safeRef = sanitizeOnboardingTemplateRef(templateRef);
  return safeRef ? `/comece?template=${encodeURIComponent(safeRef)}` : '/comece';
};

export const extractOnboardingTemplateRefFromRedirect = (redirectParam) => {
  const raw = String(redirectParam || '').trim();
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://')) return null;
  try {
    const parsed = new URL(raw, 'https://app.laudando.online');
    if (parsed.pathname !== '/novo-laudo') return null;
    const acquisitionRef = sanitizeOnboardingTemplateRef(parsed.searchParams.get('acq_template_id'));
    if (!acquisitionRef) return null;
    const editorRef = sanitizeOnboardingTemplateRef(parsed.searchParams.get('templateId'));
    if (editorRef && editorRef !== acquisitionRef) return null;
    return acquisitionRef;
  } catch {
    return null;
  }
};

const resolveSafeExplicitRedirect = (redirectParam) => {
  if (redirectParam === 'planos') return '/planos';
  const raw = String(redirectParam || '').trim();
  if (!raw) return null;
  const safe = sanitizeRedirectPath(raw);
  if (safe === '/painel' && raw !== '/painel') return null;
  return safe;
};

export const resolvePostAuthDestination = ({ mode, redirectParam } = {}) => {
  const templateRef = mode === 'signup' ? extractOnboardingTemplateRefFromRedirect(redirectParam) : null;
  if (templateRef) return buildOnboardingPath({ templateRef });
  const explicit = resolveSafeExplicitRedirect(redirectParam);
  if (explicit) return explicit;
  if (mode === 'signup') return '/comece';
  return '/painel';
};

export const readOnboardingState = (storage, userId = null) => {
  if (!storage?.getItem) return { status: 'pending', templateRef: null };
  try {
    const parsed = JSON.parse(storage.getItem(onboardingStorageKey(userId)) || '{}');
    return {
      status: VALID_STATUSES.has(parsed?.status) ? parsed.status : 'pending',
      templateRef: sanitizeOnboardingTemplateRef(parsed?.templateRef),
    };
  } catch {
    return { status: 'pending', templateRef: null };
  }
};

export const writeOnboardingState = (storage, state = {}, userId = null) => {
  if (!storage?.setItem) return;
  const current = readOnboardingState(storage, userId);
  const safeState = {
    status: VALID_STATUSES.has(state?.status) ? state.status : current.status,
    templateRef: sanitizeOnboardingTemplateRef(state?.templateRef ?? current.templateRef),
  };
  storage.setItem(onboardingStorageKey(userId), JSON.stringify(safeState));
};

export const markOnboardingActivated = (storage, userId = null) => {
  const current = readOnboardingState(storage, userId);
  if (current.status === 'activated') return false;
  writeOnboardingState(storage, { ...current, status: 'activated' }, userId);
  return true;
};
